<template>
  <div class="content">
    <v-card class="d-flex pa-1 bg" outlined>
      <div class="mr-auto d-flex"></div>
      <div class="top-button-group">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" class="mr-2" @click="add" x-small right color="primary"><v-icon small>mdi-plus-circle-outline</v-icon></v-btn>
          </template>
          <span>{{ $t('common.add') }}</span>
        </v-tooltip>
      </div>
    </v-card>
    <v-data-table ref="list" dense :headers="headers" :items="collectors" item-key="index" :loading="loading" class="elevation-1" hide-default-footer>
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small color="primary" @click="edit(item)" class="mr-2">mdi-pencil-box-multiple-outline</v-icon>
          </template>
          <span>{{ $t('common.edit') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small color="primary" @click="remove(item)">mdi-trash-can-outline</v-icon>
          </template>
          <span>{{ $t('common.delete') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.handleCollect="{ item }">
        <col-handle-collect :item="item"></col-handle-collect>
      </template>
      <template v-slot:item.handleCollector="{ item }">
        <col-handle-collector :item="item"></col-handle-collector>
      </template>
    </v-data-table>
    <component v-bind:is="component" :values="values" v-model="showForm" @insert="onInsert" />
  </div>
</template>

<script>
import { api } from '@/api/common/instances';
import bus from '@/utils/bus';
import ColHandleCollect from './ColHandleCollect';
import ColHandleCollector from './ColHandleCollector';
import CollectorForm from './CollectorForm';

export default {
  components: {
    ColHandleCollect,
    ColHandleCollector,
    CollectorForm,
  },
  data() {
    return {
      showForm: false,
      loading: true,
      component: null,
      collectors: [],
      values: null,
    };
  },
  computed: {
    classContents() {
      return {
        'contents-background-dark': this.$vuetify.theme.isDark,
      };
    },
    headers() {
      return [
        { text: this.$t('system.id'), value: 'id', sortable: false },
        { text: this.$t('system.system'), value: 'systemId', sortable: false },
        { text: this.$t('collector.stackType'), value: 'stackType', sortable: false },
        { text: this.$t('collector.restAddress'), value: 'restAddress', sortable: false },
        { text: this.$t('collector.terminalAddress'), value: 'terminalAddress', sortable: false },
        { text: '', value: 'actions', sortable: false },
        { text: this.$t('collector.handleCollect'), value: 'handleCollect', sortable: false },
        { text: this.$t('collector.handleCollector'), value: 'handleCollector', sortable: false },
      ];
    },
  },
  methods: {
    async add() {
      this.values = {};
      this.component = (await import(`./CollectorForm.vue`)).default;
      this.showForm = true;
    },
    async edit(item) {
      this.values = item;
      this.component = (await import(`./CollectorForm.vue`)).default;
      this.showForm = true;
    },
    async remove(item) {
      if (await this.$dialog.confirm(this.$t('common.delete.q'))) {
        this.loading = true;
        const response = await api.delete(`/preference/collectors/${item.id}/${item.systemId}/${item.stackType}`);
        if (response.status == 200) await this.getData();
        this.loading = false;
      }
    },
    onInsert() {
      this.getData();
    },
    setRow(list, row, rowIndex, groupColumnIndex) {
      row.setAttribute('data-row-index', rowIndex);
      row.addEventListener('mouseover', () => {
        const rowIndex = parseInt(row.getAttribute('data-row-index'));
        list[rowIndex].childNodes[groupColumnIndex].style.backgroundColor = getComputedStyle(row, null).getPropertyValue('background-color');
      });
      row.addEventListener('mouseout', () => {
        const rowIndex = parseInt(row.getAttribute('data-row-index'));
        list[rowIndex].childNodes[groupColumnIndex].style.removeProperty('background-color');
      });
    },
    setRows(list, row, rowIndex, groupColumnIndices) {
      groupColumnIndices.forEach(groupColumnIndex => this.setRow(list, row, rowIndex, groupColumnIndex));
    },
    setColumn(list, column, rowspan) {
      column.setAttribute('rowspan', rowspan);
      column.addEventListener('mouseover', event => {
        const row = event.target.parentElement;
        const index = parseInt(row.getAttribute('data-row-index'));
        for (let i = index; i < index + rowspan; i++) {
          list[i].style.backgroundColor = getComputedStyle(row, null).getPropertyValue('background-color');
        }
      });
      column.addEventListener('mouseout', event => {
        const row = event.target.parentElement;
        const index = parseInt(row.getAttribute('data-row-index'));
        for (let i = index; i < index + rowspan; i++) {
          list[i].style.removeProperty('background-color');
        }
      });
    },
    setColumns(list, columns, rowspan) {
      columns.forEach(column => this.setColumn(list, column, rowspan));
    },
    setColumnGroup(valueKey, groupKeys) {
      if (!Object.prototype.hasOwnProperty.call(this.$refs.list || {}, '$el')) return;
      let before = null;
      let groupColumns = [];
      let rowspan = 1;
      let rowIndex = 0;
      const valueColumnIndex = this.headers.findIndex(header => header.value == valueKey);
      const groupColumnIndices = groupKeys.map(groupKey => this.headers.findIndex(header => header.value == groupKey));
      const list = Array.from(this.$refs.list.$el.querySelector('tbody').querySelectorAll('tr'));
      list.forEach((row, index) => {
        const value = row.childNodes[valueColumnIndex].textContent;
        const columns = groupColumnIndices.map(groupColumnIndex => row.childNodes[groupColumnIndex]);
        if (before != value) {
          if (index) {
            this.setColumns(list, groupColumns, rowspan);
            rowIndex = index;
            rowspan = 1;
          }
          groupColumns = columns;
        } else {
          columns.forEach(column => column.remove());
          rowspan++;
        }
        this.setRows(list, row, rowIndex, groupColumnIndices);
        before = value;
      });
      this.setColumns(list, groupColumns, rowspan);
    },
    setGroupStates(data) {
      let children = [];
      let before = null;
      let groupIndex = 0;
      data.forEach((row, index) => {
        if (before != row.id) {
          if (index) {
            data[groupIndex].children = children;
            groupIndex = index;
            children = [];
          }
        }
        children.push(row);
        before = row.id;
      });
      data[groupIndex].children = children;
    },
    async getData() {
      this.loading = true;
      const { data: collectors } = await api.get('/preference/collectors');
      if (collectors.length) {
        this.collectors = collectors.map((item, index) => ({
          index,
          ...item,
        }));
        this.setGroupStates(this.collectors);
        this.$nextTick(() => this.setColumnGroup('id', ['id', 'handleCollector']));
      }
      this.loading = false;
    },
    setEventListener() {
      bus.$on('activated-collect', item => {
        this.collectors[item.seq].collecting = true;
      });
      bus.$on('activated-collector', item => {
        item.children.forEach(child => (this.collectors[child.seq].collecting = true));
      });
      bus.$on('deactivated-collect', item => {
        this.collectors[item.seq].collecting = false;
      });
      bus.$on('deactivated-collector', item => {
        item.children.forEach(child => (this.collectors[child.seq].collecting = false));
      });
      bus.$on('request-exists', item => {
        const exists = this.collectors.some(c => c.systemId == item.systemId && c.stackType == item.stackType);
        bus.$emit('response-exists', exists);
      });
    },
    removeEventListener() {
      bus.$off('activated-collect');
      bus.$off('activated-collector');
      bus.$off('deactivated-collect');
      bus.$off('deactivated-collector');
      bus.$off('request-exists');
    },
  },
  created() {
    this.getData();
    this.setEventListener();
  },
  destroyed() {
    this.removeEventListener();
  },
  mounted() {
    this.mounted = true;
  },
};
</script>

<style scoped>
.content {
  width: 800px;
  height: 300px;
}
</style>
